import React, { useEffect, useState } from 'react';
import { Check, ChevronsUpDown, UserPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';

// ── CreateNewCustomerModal ────────────────────────────────────────────────────
//
// Pops when the reviewer clicks "Create New Customer" in the row drawer.
// Pre-filled from `draft` (sourced from row.output.top_level + row.source_row).
// Every field is editable so the reviewer can correct the customer record
// before submitting to AppDirect.
//
// Props:
//   open              {boolean}
//   onOpenChange      {(boolean) => void}
//   draft             {object}  — initial field values:
//                       { advisor_company, sales_rep,
//                         customer: { name },
//                         location: { street, city, state, zip } }
//   submitting        {boolean} — disables submit while parent is saving
//   onConfirm         {(editedDraft) => Promise<void>}
//   salesReps         {{advisor, items}|null} — from drawer cache, null = loading
//   onFetchSalesReps  {(advisorName: string) => void} — trigger lazy fetch
//
// `editedDraft` passed to onConfirm:
//   { name, street, city, state, zip, advisor_company,
//     sales_rep, sales_rep_id }

export default function CreateNewCustomerModal({
  open,
  onOpenChange,
  draft,
  submitting,
  onConfirm,
  salesReps = null,
  onFetchSalesReps,
}) {
  const [repPopoverOpen, setRepPopoverOpen] = useState(false);
  // Advisor picker — searchable against the live Provider Sales roster.
  // Free-typed advisor names that aren't in the roster make customer
  // creation fail with a 422 (live 2026-07-10: "911 Communications" absent),
  // so the picker is how the operator finds a VALID advisor. GetAdvisors
  // can't be enumerated blindly (AppDirect requires `name`), but a single
  // letter is a valid, useful query — the backend sorts + caps the roster,
  // so browsing here means "type a letter and scroll" rather than "type 2+
  // characters to get anything at all" (Aidan 2026-07-29).
  const [advisorPopoverOpen, setAdvisorPopoverOpen] = useState(false);
  const [advisorQuery, setAdvisorQuery] = useState('');
  const [advisorItems, setAdvisorItems] = useState([]);
  // 'idle' (popover open, nothing typed) | 'loading' | 'success' | 'error' —
  // kept distinct so a dead session/failed fetch never renders identically
  // to "no matches" (see friendlyFailure() in PushHistorySection.js for the
  // sibling pattern on the AppDirectCall side of this module).
  const [advisorStatus, setAdvisorStatus] = useState('idle');
  const [advisorErrorMessage, setAdvisorErrorMessage] = useState('');
  // Flatten the nested draft prop into a local flat state that mirrors the
  // field layout. Re-seed whenever `draft` changes (i.e. a new row is opened).
  const [fields, setFields] = useState(() => flattenDraft(draft));

  // Debounced roster search as the operator types — fires on 1+ characters.
  useEffect(() => {
    const query = advisorQuery.trim();
    if (!advisorPopoverOpen || !query) {
      setAdvisorItems([]);
      setAdvisorStatus('idle');
      setAdvisorErrorMessage('');
      return undefined;
    }
    let alive = true;
    setAdvisorStatus('loading');
    const t = setTimeout(async () => {
      try {
        const res = await api.get(
          `${API_URL}/api/match/appdirect/advisors?name=${encodeURIComponent(query)}`
        );
        const data = await res.json();
        if (!alive) return;
        if (res.ok) {
          setAdvisorItems(data?.items ?? []);
          setAdvisorStatus('success');
        } else {
          setAdvisorItems([]);
          setAdvisorStatus('error');
          setAdvisorErrorMessage(data?.detail || `Advisor search failed (HTTP ${res.status}).`);
        }
      } catch (err) {
        if (!alive) return;
        setAdvisorItems([]);
        setAdvisorStatus('error');
        setAdvisorErrorMessage(err?.message || 'Advisor search failed unexpectedly.');
      }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [advisorQuery, advisorPopoverOpen]);

  useEffect(() => {
    if (open) {
      setFields(flattenDraft(draft));
    }
  }, [open, draft]);

  function flattenDraft(d) {
    if (!d) {
      return {
        name: '',
        advisor_company: '',
        sales_rep: '',
        sales_rep_id: null,
        street: '',
        city: '',
        state: '',
        zip: '',
      };
    }
    return {
      name: d.customer?.name ?? d.name ?? '',
      advisor_company: d.advisor_company ?? '',
      sales_rep: d.sales_rep ?? '',
      sales_rep_id: null, // reset — user must pick from combobox in this session
      street: d.location?.street ?? d.street ?? '',
      city: d.location?.city ?? d.city ?? '',
      state: d.location?.state ?? d.state ?? '',
      zip: d.location?.zip ?? d.zip ?? '',
    };
  }

  // Trigger a lazy rep fetch when the advisor company is known but reps not yet loaded
  useEffect(() => {
    if (open && fields.advisor_company && salesReps === null && onFetchSalesReps) {
      onFetchSalesReps(fields.advisor_company);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fields.advisor_company]);

  function set(key, value) {
    setFields((prev) => ({ ...prev, [key]: value }));
  }

  function handleCancel() {
    onOpenChange(false);
  }

  async function handleSubmit() {
    if (missingRequired) return;
    await onConfirm(fields);
  }

  // AppDirect's customer create requires all of these server-side (name,
  // address, city, state, zipCode) plus a resolvable advisor company — an
  // empty one 400/500s the push, so gate submit on the full set.
  const REQUIRED_KEYS = ['name', 'advisor_company', 'street', 'city', 'state', 'zip'];
  const missingRequired = REQUIRED_KEYS.some((k) => !fields[k]?.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[540px] bg-boss-surface border border-boss-border text-boss-text"
        data-testid="create-customer-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-boss-text">
            <UserPlus className="h-5 w-5 text-boss-text-secondary" />
            Create New AppDirect Customer
          </DialogTitle>
          <DialogDescription className="text-boss-text-secondary">
            Pre-filled from the comparison screen. Edit any field before submitting — these
            values will be sent to create the customer in AppDirect.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-4 gap-y-4 py-2">
          {/* Customer Name */}
          <div className="col-span-2">
            <Label htmlFor="ccm-name" className="text-xs text-boss-text-secondary mb-1.5 block">
              Customer Name <span className="text-[var(--boss-danger-text)]">*</span>
            </Label>
            <Input
              id="ccm-name"
              className="boss-input"
              value={fields.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Customer name"
              data-testid="create-customer-name-input"
            />
          </div>

          {/* AppDirect Customer ID — read-only, assigned on creation */}
          <div className="col-span-2">
            <Label className="text-xs text-boss-text-secondary mb-1.5 block">
              AppDirect Customer ID
            </Label>
            <Input
              className="boss-input opacity-60 cursor-not-allowed"
              value=""
              readOnly
              disabled
              placeholder="—"
              data-testid="create-customer-appdirect-id-input"
            />
            <p className="text-[11px] text-boss-text-secondary italic mt-1">
              Will be assigned by AppDirect when the new customer is created.
            </p>
          </div>

          {/* Advisor Company — roster-searchable picker. The typed report
              value stays visible, but only a roster pick guarantees the
              create succeeds (non-roster advisors 422 at push). */}
          <div className="col-span-2">
            <Label className="text-xs text-boss-text-secondary mb-1.5 block">
              Advisor Company <span className="text-[var(--boss-danger-text)]">*</span>
            </Label>
            <Popover open={advisorPopoverOpen} onOpenChange={setAdvisorPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  role="combobox"
                  aria-expanded={advisorPopoverOpen}
                  data-testid="create-customer-advisor-company-input"
                  className={`w-full flex items-center justify-between rounded-md border border-boss-border px-3 py-2 text-sm text-left boss-input ${!fields.advisor_company ? 'text-boss-text-muted' : ''}`}
                >
                  <span className="truncate">
                    {fields.advisor_company || 'Search Provider Sales advisors…'}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-boss-text-muted" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Type a letter to browse, or search by name…"
                    value={advisorQuery}
                    onValueChange={setAdvisorQuery}
                    data-testid="create-customer-advisor-search-input"
                  />
                  <CommandList>
                    <CommandEmpty className="py-4 px-3 text-left text-sm">
                      {advisorStatus === 'loading' && (
                        <span className="text-boss-text-muted">Searching…</span>
                      )}
                      {advisorStatus === 'idle' && (
                        <span className="text-boss-text-muted">
                          Type a letter to browse Provider Sales advisors A-Z, or keep typing to narrow it down.
                        </span>
                      )}
                      {advisorStatus === 'error' && (
                        <div
                          className="boss-alert-danger rounded-md px-3 py-2 text-xs leading-snug"
                          data-testid="create-customer-advisor-error"
                        >
                          Couldn’t reach the advisor roster: {advisorErrorMessage}
                        </div>
                      )}
                      {advisorStatus === 'success' && (
                        <span className="text-boss-text-muted">
                          No roster match — a non-roster advisor will fail customer creation.
                        </span>
                      )}
                    </CommandEmpty>
                    <CommandGroup>
                      {advisorItems.map((a) => (
                        <CommandItem
                          key={a.internal_id}
                          value={String(a.internal_id)}
                          onSelect={() => {
                            set('advisor_company', a.name);
                            setAdvisorPopoverOpen(false);
                            // The rep roster is advisor-scoped — refetch for the pick.
                            onFetchSalesReps?.(a.name);
                          }}
                          data-testid={`create-customer-advisor-option-${a.internal_id}`}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${fields.advisor_company === a.name ? 'opacity-100' : 'opacity-0'}`}
                          />
                          {a.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Sales Rep — combobox or free-text fallback */}
          <div className="col-span-2">
            <Label className="text-xs text-boss-text-secondary mb-1.5 block">
              Sales Rep
            </Label>
            {salesReps && salesReps.items.length > 0 ? (
              <Popover open={repPopoverOpen} onOpenChange={setRepPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={repPopoverOpen}
                    data-testid="create-customer-sales-rep-input"
                    className={`w-full flex items-center justify-between rounded-md border border-boss-border px-3 py-2 text-sm text-left boss-input ${!fields.sales_rep ? 'text-boss-text-muted' : ''}`}
                  >
                    <span className="truncate">
                      {fields.sales_rep || 'Search reps…'}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-boss-text-muted" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[320px] p-0 bg-boss-surface border border-boss-border text-boss-text"
                  align="start"
                >
                  <Command>
                    <CommandInput
                      placeholder="Search reps…"
                      className="h-9 text-sm text-boss-text bg-boss-surface"
                    />
                    <CommandList>
                      <CommandEmpty className="py-4 text-center text-sm text-boss-text-muted">
                        No reps found.
                      </CommandEmpty>
                      <CommandGroup>
                        {salesReps.items.map((rep) => (
                          <CommandItem
                            key={rep.id}
                            value={rep.name}
                            onSelect={() => {
                              set('sales_rep', rep.name);
                              set('sales_rep_id', rep.id);
                              setRepPopoverOpen(false);
                            }}
                            className="text-sm text-boss-text data-[selected=true]:bg-boss-elevated flex flex-col items-start gap-0.5 py-2"
                          >
                            <div className="flex items-center gap-2">
                              <Check
                                className={`h-4 w-4 shrink-0 ${fields.sales_rep_id === rep.id ? 'opacity-100' : 'opacity-0'}`}
                              />
                              <span>{rep.name}</span>
                            </div>
                            {rep.email && (
                              <span className="pl-6 text-xs text-boss-text-muted leading-tight">{rep.email}</span>
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              <Input
                id="ccm-sales-rep"
                className="boss-input"
                value={fields.sales_rep}
                onChange={(e) => set('sales_rep', e.target.value)}
                placeholder={
                  salesReps === null
                    ? 'Loading reps…'
                    : 'No reps returned — enter manually, or leave blank to use template default'
                }
                disabled={salesReps === null}
                data-testid="create-customer-sales-rep-input"
              />
            )}
            {salesReps && salesReps.items.length === 0 && salesReps.advisor && (
              <p className="mt-1 text-[11px] text-boss-text-muted italic">
                No reps returned for {salesReps.advisor.name} — enter manually.
              </p>
            )}
          </div>

          {/* Street */}
          <div className="col-span-2">
            <Label htmlFor="ccm-street" className="text-xs text-boss-text-secondary mb-1.5 block">
              Street <span className="text-[var(--boss-danger-text)]">*</span>
            </Label>
            <Input
              id="ccm-street"
              className="boss-input"
              value={fields.street}
              onChange={(e) => set('street', e.target.value)}
              data-testid="create-customer-street-input"
            />
          </div>

          {/* City */}
          <div>
            <Label htmlFor="ccm-city" className="text-xs text-boss-text-secondary mb-1.5 block">
              City <span className="text-[var(--boss-danger-text)]">*</span>
            </Label>
            <Input
              id="ccm-city"
              className="boss-input"
              value={fields.city}
              onChange={(e) => set('city', e.target.value)}
              data-testid="create-customer-city-input"
            />
          </div>

          {/* State + Zip side by side */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="ccm-state" className="text-xs text-boss-text-secondary mb-1.5 block">
                State <span className="text-[var(--boss-danger-text)]">*</span>
              </Label>
              <Input
                id="ccm-state"
                className="boss-input"
                value={fields.state}
                onChange={(e) => set('state', e.target.value)}
                data-testid="create-customer-state-input"
              />
            </div>
            <div>
              <Label htmlFor="ccm-zip" className="text-xs text-boss-text-secondary mb-1.5 block">
                ZIP <span className="text-[var(--boss-danger-text)]">*</span>
              </Label>
              <Input
                id="ccm-zip"
                className="boss-input"
                value={fields.zip}
                onChange={(e) => set('zip', e.target.value)}
                data-testid="create-customer-zip-input"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={submitting}
            className="border-boss-border text-boss-text-secondary hover:text-boss-text hover:bg-boss-elevated"
            data-testid="create-customer-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || missingRequired}
            className="btn-primary flex items-center gap-1.5"
            data-testid="create-customer-submit-button"
          >
            <UserPlus className="h-4 w-4" />
            {submitting ? 'Creating…' : 'Create Customer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
